import { DataTableColumnHeader } from "@/components/DataTable/Columns/DataTableColumnHeader";
import type { HelperOptions } from "@/components/DataTable/types";
import TooltipAlert from "@/components/Tooltip/TooltipAlert";
import { TooltipWrapper } from "@/components/Tooltip/TooltipWrapper";
import type { ColumnDef } from "@tanstack/react-table";

type NestedKeys<T> = T extends object
  ? {
      [K in keyof T]: K extends string
        ? `${K}` | `${K}.${NestedKeys<T[K]>}`
        : never;
    }[keyof T]
  : never;

// i18nKeyまたはdisplayLabelのどちらか一方が必須の型定義
type SimpleCellTextOptions =
  | (HelperOptions & { i18nKey: NonNullable<HelperOptions["i18nKey"]> })
  | (HelperOptions & {
      displayLabel: NonNullable<HelperOptions["displayLabel"]>;
    });

/** テキストを表示するカラム */
export function SimpleCellTextColumn<T>(
  id: NestedKeys<T> & string,
  options: SimpleCellTextOptions,
): ColumnDef<T> {
  const { i18nKey, displayLabel, minWidth, sortable = false } = options;
  return {
    accessorKey: id,
    meta: { i18nKey, displayLabel, thStyle: { minWidth } },
    enableSorting: sortable,
    header: DataTableColumnHeader,
    cell: ({ getValue }) => {
      const cellData = getValue();
      const value = cellData.value || "-";
      const hasErrors = (cellData.errors?.length ?? 0) > 0;

      const TEXT_LENGTH_THRESHOLD_FOR_TOOLTIP = 10;
      const isShortText =
        value === "-" || value.length <= TEXT_LENGTH_THRESHOLD_FOR_TOOLTIP;

      // エラーがある場合の表示
      if (hasErrors) {
        return (
          <TooltipAlert
            content={cellData.errors?.join("\n") ?? ""}
            value={value}
          />
        );
      }

      // 短いテキストの場合（Tooltipなし）
      if (isShortText) {
        return (
          <div className="w-[120px] overflow-hidden text-ellipsis whitespace-nowrap">
            {value}
          </div>
        );
      }

      // 長いテキストの場合（Tooltipあり）
      return (
        <TooltipWrapper delayDuration={500} content={value}>
          <div className="w-[120px] overflow-hidden text-ellipsis whitespace-nowrap">
            {value}
          </div>
        </TooltipWrapper>
      );
    },
  } satisfies ColumnDef<
    T,
    {
      value: string;
      errors?: string[];
    }
  >;
}
